import {
  ChannelPriceAndPreorderData,
  IChannelPriceAndPreorderArgs,
} from "@dashboard/channels/utils";
import { Divider } from "@dashboard/components/Divider";
import { FormsetData } from "@dashboard/hooks/useFormset";
import { PropsWithChildren } from "react";

import { ProductChannelListing } from "./../types";
import { ChannelsListItem } from "./ChannelsListItem";
import { getToggledChannelIds } from "./getToggledChannelIds";
import { useFilteredChannelListing } from "./useFilteredChannelListing";
import CardContainer from "./VariantDetailsChannelsAvailabilityCardContainer";

interface AvailabilityCardProps {
  allAvailableListings: FormsetData<ChannelPriceAndPreorderData, IChannelPriceAndPreorderArgs>;
  productChannelListings: ProductChannelListing | undefined;
  // When provided, the card renders a per-variant "For sale in this channel"
  // toggle for every channel the product is listed in and calls this with the
  // full set of channel ids the variant should be listed in.
  onListedChannelsChange?: (channelIds: string[]) => void;
  disabled?: boolean;
}

export const AvailabilityCard = ({
  allAvailableListings,
  productChannelListings,
  onListedChannelsChange,
  disabled,
  children,
}: PropsWithChildren<AvailabilityCardProps>) => {
  const filteredListings = useFilteredChannelListing({
    allAvailableListings,
    channelListing: productChannelListings,
  });
  const hasListingControl = typeof onListedChannelsChange === "function";
  const listedChannelIds = allAvailableListings.map(listing => listing.id);
  // In variant mode show every channel the product is in (so the toggle can add
  // as well as remove); otherwise keep the read-only listed-only view.
  const listingsToRender = hasListingControl ? (productChannelListings ?? []) : filteredListings;

  const handleToggle = (channelId: string, next: boolean) => {
    onListedChannelsChange?.(getToggledChannelIds(listedChannelIds, channelId, next));
  };

  if (listingsToRender.length === 0) {
    return <CardContainer cardTitle={children}>{null}</CardContainer>;
  }

  return (
    <CardContainer cardTitle={children}>
      {listingsToRender.map((listing: ProductChannelListing[0]) => (
        <ChannelsListItem
          {...listing}
          id={listing.channel.id}
          name={listing.channel.name}
          key={listing.channel.id}
          {...(hasListingControl
            ? {
                isListed: listedChannelIds.includes(listing.channel.id),
                onToggleListed: (next: boolean) => handleToggle(listing.channel.id, next),
                disabled,
              }
            : {})}
        />
      ))}
      <Divider />
    </CardContainer>
  );
};
